import React, { memo } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody } from "reactstrap";
import "../../styles/ModalShared.css";
import {
  COUNTDOWN_ANNOUNCE_AT_SECONDS,
  SESSION_MESSAGES,
  SESSION_WARNING_REASONS,
} from "../../constants/Session";

const TITLES = {
  [SESSION_WARNING_REASONS.IDLE]: SESSION_MESSAGES.WARNING_TITLE,
  [SESSION_WARNING_REASONS.RENEWAL_FAILED]: SESSION_MESSAGES.RENEWAL_FAILED_TITLE,
};

const formatCountdown = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainder = String(seconds % 60).padStart(2, "0");
  return `${minutes}:${remainder}`;
};

// The latest announcement threshold the countdown has reached, or null
// before the first one. The text only changes when a threshold is crossed,
// so the polite live region speaks once per threshold, not once a second.
const getAnnouncedSeconds = (secondsLeft) =>
  COUNTDOWN_ANNOUNCE_AT_SECONDS.filter((threshold) => secondsLeft <= threshold).pop() ?? null;

// Blocking prompt: no close icon, no backdrop click, no Escape. The user
// either continues (renews the token) or cancels (closes the popup); the
// countdown logs out on its own when it reaches zero. reactstrap puts role and labelledBy on
// the outer dialog element, so the alertdialog is named by its title and
// wraps the buttons too.
const SessionTimeoutWarningModalComponent = ({
  isOpen,
  reason,
  secondsLeft,
  isRenewing,
  onContinue,
  onCancel,
}) => {
  const announcedSeconds = getAnnouncedSeconds(secondsLeft);
  return (
    <Modal
      zIndex={9999999}
      isOpen={isOpen}
      centered
      className="custom-modal"
      backdrop="static"
      keyboard={false}
      role="alertdialog"
      labelledBy="session-timeout-title"
    >
      <ModalBody className="p-6 pt-2 text-center">
        <h5 id="session-timeout-title" className="modal-title mb-2">
          {TITLES[reason] || SESSION_MESSAGES.WARNING_TITLE}
        </h5>
        <p className="modal-message mb-0">
          {SESSION_MESSAGES.WARNING_BODY}{" "}
          <strong data-testid="session-timeout-countdown">{formatCountdown(secondsLeft)}</strong>.{" "}
          {SESSION_MESSAGES.WARNING_QUESTION}
        </p>
        <span className="visually-hidden" role="status" aria-live="polite">
          {announcedSeconds !== null && SESSION_MESSAGES.COUNTDOWN_ANNOUNCEMENT(announcedSeconds)}
        </span>
      </ModalBody>
      <div className="custom-footer">
        <button className="modal-btn cancel-btn" onClick={onCancel} disabled={isRenewing}>
          {SESSION_MESSAGES.CANCEL_BUTTON}
        </button>
        <div className="divider" />
        <button className="modal-btn continue-btn" onClick={onContinue} disabled={isRenewing} autoFocus>
          {isRenewing ? SESSION_MESSAGES.RENEWING_BUTTON : SESSION_MESSAGES.CONTINUE_BUTTON}
        </button>
      </div>
    </Modal>
  );
};

SessionTimeoutWarningModalComponent.displayName = "SessionTimeoutWarningModal";

const SessionTimeoutWarningModal = memo(SessionTimeoutWarningModalComponent);

SessionTimeoutWarningModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  reason: PropTypes.oneOf(Object.values(SESSION_WARNING_REASONS)),
  secondsLeft: PropTypes.number.isRequired,
  isRenewing: PropTypes.bool,
  onContinue: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default SessionTimeoutWarningModal;
